import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import axios from 'axios'
import { computed, ref, watch, type Ref } from 'vue'

import { logError } from '@/logger'
import { OlympusClient } from '@/OlympusClient'
import { useAnalytics } from '@/utils/useAnalytics'
import { useNotify } from '@/utils/useNotify'

import { MEDIA_QUERY_KEYS } from './constants'
import type {
  Media,
  MediaAnalysis,
  MediaFinding,
  MediaWithPurpose,
} from './types'

const isKnownMediaError = (
  error: unknown
): error is { code: string; message: string } => {
  if (axios.isAxiosError(error)) {
    const code = error.response?.data?.code
    return typeof code === 'string'
  }
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    typeof (error as { code: unknown }).code === 'string'
  )
}

const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.message ?? error.message
  }
  if (error instanceof Error) {
    return error.message
  }
  return 'Unknown error'
}

interface UseMediaAnalysisParams {
  menuItemId: Ref<string>
  brandId: Ref<string>
  currentImageUrl: Ref<string | null>
  itemName: Ref<string>
  itemDescription: Ref<string | null>
}

interface UseMediaAnalysisReturn {
  primaryMedia: Ref<Media | null>
  analysis: Ref<MediaAnalysis | null>
  isLoading: Ref<boolean>
  isBootstrapping: Ref<boolean>
  error: Ref<Error | null>
  hasIssues: Ref<boolean>
  problemFindings: Ref<MediaFinding[]>
  analysisTimestamp: Ref<string | null>
  refetch: () => Promise<void>
  triggerAnalysis: () => Promise<void>
}

/**
 * Composable for media analysis integration with Media Library API
 * Handles media bootstrapping, analysis fetching, and triggering
 * @param params - Configuration parameters
 */
export function useMediaAnalysis(
  params: UseMediaAnalysisParams
): UseMediaAnalysisReturn {
  const notify = useNotify()
  const { track } = useAnalytics()
  const queryClient = useQueryClient()
  const isBootstrapping = ref(false)
  const primaryMediaId = ref<string | null>(null)
  const analysisStartTime = ref<number | null>(null)

  const {
    data: mediaListData,
    isLoading: isLoadingMediaList,
    error: mediaListError,
  } = useQuery({
    enabled: computed(() => !!params.menuItemId.value),
    queryFn: async () => {
      const response = await OlympusClient.media.v1.menuItem.menuItem.listMedia(
        {
          params: { itemId: params.menuItemId.value },
          query: { purpose: 'primary_image' },
        }
      )
      return response
    },
    queryKey: computed(() =>
      MEDIA_QUERY_KEYS.menuItemMedia(params.menuItemId.value, 'primary_image')
    ),
    staleTime: 0,
  })

  watch(
    mediaListData,
    async (data) => {
      if (!data) return

      const primaryMedia = data.results.find(
        (item: MediaWithPurpose) => item.purpose === 'primary_image'
      )

      if (primaryMedia) {
        primaryMediaId.value = primaryMedia.media.id
      } else if (params.currentImageUrl.value && !isBootstrapping.value) {
        await bootstrapMedia()
      }
    },
    { immediate: true }
  )

  /**
   * Creates a media record from legacy URL and attaches to menu item
   */
  const bootstrapMedia = async () => {
    if (!params.currentImageUrl.value || !params.brandId.value) return

    isBootstrapping.value = true

    try {
      const response = await OlympusClient.media.v1.createMedia({
        body: {
          brandId: params.brandId.value,
          comments: undefined,
          legacyImageId: undefined,
          mimeType: 'image/jpeg',
          source: 'legacy-import',
          url: params.currentImageUrl.value,
        },
      })

      primaryMediaId.value = response.media.id

      await OlympusClient.media.v1.menuItem.menuItem.attachMedia({
        body: { mediaId: response.media.id, purpose: 'primary_image' },
        params: { itemId: params.menuItemId.value },
      })

      await queryClient.invalidateQueries({
        queryKey: MEDIA_QUERY_KEYS.menuItemMedia(
          params.menuItemId.value,
          'primary_image'
        ),
      })
    } catch (error) {
      logError(error)
      if (isKnownMediaError(error)) {
        notify(
          `Unable to set up image analysis: ${getErrorMessage(error)}`,
          'error'
        )
      } else {
        notify('Failed to initialize image analysis', 'error')
      }
    } finally {
      isBootstrapping.value = false
    }
  }

  const {
    data: analysisData,
    isLoading: isLoadingAnalysis,
    error: analysisError,
    refetch: refetchAnalysis,
  } = useQuery({
    enabled: computed(() => !!primaryMediaId.value),
    queryFn: async () => {
      if (!primaryMediaId.value) {
        throw new Error('No media ID available')
      }

      try {
        const response =
          await OlympusClient.media.v1.media.analysis.getLatestAnalysis({
            params: { mediaId: primaryMediaId.value },
          })
        return response.analysis
      } catch (error: unknown) {
        if (
          axios.isAxiosError(error) &&
          error.response?.data?.code === 'no-analysis-found'
        ) {
          return null
        }
        throw error
      }
    },
    queryKey: computed(() =>
      primaryMediaId.value
        ? MEDIA_QUERY_KEYS.analysis(primaryMediaId.value)
        : ['media-analysis', null]
    ),
    retry: false,
    staleTime: 0,
  })

  const { mutateAsync: triggerAnalysisMutation, isPending: isAnalyzing } =
    useMutation({
      mutationFn: async (isReanalyze: boolean) => {
        if (!primaryMediaId.value) {
          throw new Error('No media ID available')
        }

        analysisStartTime.value = Date.now()

        track('image_analysis_triggered', {
          isReanalyze,
          menuItemId: params.menuItemId.value,
        })

        const response = await OlympusClient.media.v1.media.analyze({
          body: {
            itemDescription: params.itemDescription.value ?? undefined,
            itemName: params.itemName.value,
          },
          params: { mediaId: primaryMediaId.value },
        })

        return response.analysis
      },
      onError: (error) => {
        logError(error)
        notify('Failed to analyze image', 'error')
      },
      onSuccess: async (analysis) => {
        if (primaryMediaId.value) {
          const durationMs = analysisStartTime.value
            ? Date.now() - analysisStartTime.value
            : undefined

          const problemCount = analysis.findings.filter(
            (f) => f.type === 'problem'
          ).length

          track('image_analysis_completed', {
            durationMs,
            issueCount: problemCount,
            menuItemId: params.menuItemId.value,
          })

          queryClient.setQueryData(
            MEDIA_QUERY_KEYS.analysis(primaryMediaId.value),
            analysis
          )
        }
      },
    })

  const primaryMedia = computed(() => {
    if (!mediaListData.value) return null

    const primaryItem = mediaListData.value.results.find(
      (item: MediaWithPurpose) => item.purpose === 'primary_image'
    )

    return primaryItem?.media ?? null
  })

  const problemFindings = computed(() => {
    if (!analysisData.value) return []
    return analysisData.value.findings.filter((f) => f.type === 'problem')
  })

  const hasIssues = computed(() => problemFindings.value.length > 0)

  const analysisTimestamp = computed(() => {
    return analysisData.value?.performedAt ?? null
  })

  const isLoading = computed(
    () =>
      isLoadingMediaList.value ||
      isLoadingAnalysis.value ||
      isAnalyzing.value ||
      isBootstrapping.value
  )

  const error = computed(() => mediaListError.value ?? analysisError.value)

  const refetch = async () => {
    await refetchAnalysis()
  }

  const triggerAnalysis = async () => {
    await triggerAnalysisMutation(true)
  }

  return {
    analysis: computed(() => analysisData.value ?? null),
    analysisTimestamp,
    error,
    hasIssues,
    isBootstrapping,
    isLoading,
    primaryMedia,
    problemFindings,
    refetch,
    triggerAnalysis,
  }
}
